import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CsvUpload } from '@/components/import/CsvUpload';
import { ImportPreview } from '@/components/import/ImportPreview';
import { ImportResults } from '@/components/import/ImportResults';
import { PageTitle } from '@/components/common/PageTitle';

export type ImportStep = 'upload' | 'preview' | 'results';

export interface ParsedProspectData {
  company: string;
  contactName: string;
  email: string;
  phone?: string;
  stage?: string;
  notes?: string;
}

export interface ImportError {
  row: number;
  field?: string;
  message: string;
  data?: ParsedProspectData;
}

export interface ImportState {
  step: ImportStep;
  data: ParsedProspectData[];
  errors: ImportError[];
  results?: {
    successful: number;
    failed: number;
    errors: ImportError[];
    createdProspects: string[];
  };
}

export function Import() {
  const [importState, setImportState] = useState<ImportState>({
    step: 'upload',
    data: [],
    errors: [],
  });

  const handleFileProcessed = (data: ParsedProspectData[], errors: ImportError[]) => {
    setImportState({
      step: 'preview',
      data,
      errors,
    });
  };

  const handleImportComplete = (results: ImportState['results']) => {
    setImportState(prev => ({
      ...prev,
      step: 'results',
      results,
    }));
  };

  const handleStartOver = () => {
    setImportState({
      step: 'upload',
      data: [],
      errors: [],
    });
  };

  const renderStep = () => {
    switch (importState.step) {
      case 'upload':
        return <CsvUpload onFileProcessed={handleFileProcessed} />;
      case 'preview':
        return (
          <ImportPreview
            data={importState.data}
            errors={importState.errors}
            onImportComplete={handleImportComplete}
            onCancel={handleStartOver}
          />
        );
      case 'results':
        return (
          <ImportResults
            results={importState.results!}
            onStartOver={handleStartOver}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <PageTitle title="Import" />
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>Import Prospects</CardTitle>
          <CardDescription>
            Upload a CSV file to bulk import prospects into your pipeline.
            Required columns: company, contactName, email. Optional: phone, stage, notes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderStep()}
        </CardContent>
      </Card>
    </div>
  );
}